import _ from 'lodash'
import moment, { Moment } from 'moment'
import SignatureCanvas from 'react-signature-canvas'

import React, { useEffect, useRef, useState } from 'react'
import { Field, InjectedFormProps, change, reduxForm } from 'redux-form'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'

import { Option as DropdownOption } from 'components/InfiniteDropdowns/InfiniteDropdownsModel'
import { Option } from 'constants/models'
import { SelectedImageProps } from 'components/PhotoUpload/PhotoUpload'
import { STATUS_OPTIONS_VALUES } from 'services/users/constants'
import { Child } from 'services/child/models'

import { isMaxLengthLessThan, isRequired } from 'utils/fieldValidation'

import { Avatar, Banner, Form, Hr, Icon, Section, Space, Spinner, Typography } from 'components'

import i18n from 'translations'

import {
  StyledCarerContact,
  StyledCarerContactItem,
  StyledCarerContainer,
  StyledCarerItemContainer,
  StyledFieldWrapper,
} from './AddEmergencyDoseModalFormStyled'

const isMaxLengthLessThan50 = isMaxLengthLessThan(50)

export interface AddEmergencyDoseFormValues {
  administerSignature?: SelectedImageProps
  administratedBy?: Option
  child?: DropdownOption
  dose?: string
  expiryDate?: Date | string
  medication?: string
  methodOfAdministration?: string
  otherNotes?: string
  parent?: Option
  parentSignature?: SelectedImageProps
  reason?: string
  sideEffects?: string
  takenAt?: Date | string | Moment
  timeOfAdministration?: moment.Moment | number
  wasParentContacted?: boolean
  witness?: Option
  witnessSignature?: SelectedImageProps
}

interface AddEmergencyDoseModalFormProps {
  child?: Child
  formValues: AddEmergencyDoseFormValues
  isChildContext: boolean
  isChildDetailLoading?: boolean
  isDiaryContext?: boolean
  isResettingChild?: boolean
  isSubmitting: boolean
  onCancelClick: () => void
  onSubmit: (fields: AddEmergencyDoseFormValues) => void
}

export const ADD_EMERGENCY_DOSE_FORM = 'AddEmergencyDoseForm'

const mapDispatch = {
  changeValue: (field, value) => change(ADD_EMERGENCY_DOSE_FORM, field, value),
}

const connector = connect(null, mapDispatch)

type PropsFromRedux = ConnectedProps<typeof connector>

type FinalProps = InjectedFormProps<AddEmergencyDoseFormValues, AddEmergencyDoseModalFormProps>
  & AddEmergencyDoseModalFormProps
  & PropsFromRedux

const AddEmergencyDoseModalForm: React.FC<FinalProps> = ({
  changeValue,
  child,
  formValues,
  handleSubmit,
  isChildContext,
  isChildDetailLoading,
  isDiaryContext,
  isResettingChild,
  isSubmitting,
  onCancelClick,
  onSubmit,
}) => {
  const [isInitialized, setIsInitialized] = useState<boolean>(false)

  const { administratedBy, child: formChild, wasParentContacted, witness } = formValues || {}

  const administerSignatureRef = useRef<typeof SignatureCanvas>()
  const witnessSignatureRef = useRef<typeof SignatureCanvas>()

  useEffect(() => {
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    if (isInitialized && administerSignatureRef.current) {
      administerSignatureRef.current.clearSignature()
    }

    if (!administratedBy) {
      changeValue('administerSignature', null)
    }
  }, [administratedBy])

  useEffect(() => {
    if (isInitialized && witnessSignatureRef.current) {
      witnessSignatureRef.current.clearSignature()
    }

    if (!witness) {
      changeValue('witnessSignature', null)
    }
  }, [witness])

  const renderSignature = () => (
    <Section.SubSection title={i18n.t('modals:Administration:AddEmergencyDose:Form:Signatures:title')}>
      <Form.Row
        label={i18n.t('modals:Administration:AddEmergencyDose:Form:Signatures:StaffDropdown:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        <Field
          component={Form.InfiniteDropdowns.Memberships}
          name="administratedBy"
          placeholder={i18n.t('modals:Administration:AddEmergencyDose:Form:Signatures:StaffDropdown:placeholder')}
          status={STATUS_OPTIONS_VALUES.ACTIVE}
          validate={isRequired}
          searchable
        />
      </Form.Row>
      {administratedBy && (
        <Form.Row
          label={i18n.t('modals:Administration:AddEmergencyDose:Form:Signatures:StaffSignature:label')}
          required
          verticalLabel
        >
          <Field
            component={Form.SignaturePad}
            disabled={!administratedBy}
            name="administerSignature"
            signatureRef={administerSignatureRef}
            validate={isRequired}
            searchable
          />
        </Form.Row>
      )}
      <Form.Row
        label={i18n.t('modals:Administration:AddEmergencyDose:Form:Signatures:WitnessDropdown:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        <Field
          component={Form.InfiniteDropdowns.Memberships}
          name="witness"
          placeholder={i18n.t('modals:Administration:AddEmergencyDose:Form:Signatures:WitnessDropdown:placeholder')}
          status={STATUS_OPTIONS_VALUES.ACTIVE}
          validate={isRequired}
          searchable
        />
      </Form.Row>
      {witness && (
        <Form.Row
          label={i18n.t('modals:Administration:AddEmergencyDose:Form:Signatures:WitnessSignature:label')}
          required
          verticalLabel
        >
          <Field
            component={Form.SignaturePad}
            disabled={!witness}
            name="witnessSignature"
            signatureRef={witnessSignatureRef}
            validate={isRequired}
            searchable
          />
        </Form.Row>
      )}
    </Section.SubSection>
  )

  const renderMedicationDetails = () => (
    <Section.SubSection
      margin="40px 0 15px"
      title={i18n.t('modals:Administration:AddEmergencyDose:Form:MedicationDetails:title')}
    >
      <Form.Row
        label={i18n.t('modals:Administration:AddEmergencyDose:Form:MedicationDetails:Medication:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        <Field
          component={Form.TextField}
          name="medication"
          placeholder={i18n.t('modals:Administration:AddEmergencyDose:Form:MedicationDetails:Medication:placeholder')}
          validate={[isRequired, isMaxLengthLessThan50]}
        />
      </Form.Row>
      <Form.Row
        label={i18n.t('modals:Administration:AddEmergencyDose:Form:MedicationDetails:ReasonForMedication:label')}
        width={{ field: '100%' }}
        required
        verticalLabel
      >
        <Field
          component={Form.TextAreaField}
          name="reason"
          placeholder={i18n.t(
            'modals:Administration:AddEmergencyDose:Form:MedicationDetails:ReasonForMedication:placeholder',
          )}
          validate={isRequired}
        />
      </Form.Row>
      <Form.Row
        label={i18n.t('modals:Administration:AddEmergencyDose:Form:MedicationDetails:MethodOfAdministration:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        <Field
          component={Form.TextField}
          name="methodOfAdministration"
          placeholder={i18n.t(
            'modals:Administration:AddEmergencyDose:Form:MedicationDetails:MethodOfAdministration:placeholder',
          )}
          validate={isRequired}
        />
      </Form.Row>
      <Form.Row
        label={i18n.t('modals:Administration:AddEmergencyDose:Form:MedicationDetails:Dose:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        <Field
          component={Form.TextField}
          name="dose"
          placeholder={i18n.t(
            'modals:Administration:AddEmergencyDose:Form:MedicationDetails:Dose:placeholder',
          )}
          validate={isRequired}
        />
      </Form.Row>
      <Form.Row
        label={i18n.t('modals:Administration:AddEmergencyDose:Form:MedicationDetails:ExpiryDate:label')}
        required
        verticalLabel
      >
        <Field
          component={Form.DatePicker}
          name="expiryDate"
          validate={isRequired}
        />
      </Form.Row>
      <Form.Row
        label={i18n.t('modals:Administration:AddEmergencyDose:Form:MedicationDetails:SideEffects:label')}
        width={{ field: '100%' }}
        verticalLabel
      >
        <Field
          component={Form.TextAreaField}
          name="sideEffects"
        />
      </Form.Row>
      <Form.Row
        label={i18n.t('modals:Administration:AddEmergencyDose:Form:MedicationDetails:OtherNotes:label')}
        width={{ field: '100%' }}
        verticalLabel
      >
        <Field
          component={Form.TextAreaField}
          name="otherNotes"
        />
      </Form.Row>
    </Section.SubSection>
  )

  const renderCarer = ({ carer }, i) => {
    const { firstName, mobile, photo, surname, telephone, workTelephone } = carer

    const phoneNumbers = []

    if (mobile) {
      phoneNumbers.push(mobile)
    }

    if (telephone) {
      phoneNumbers.push(telephone)
    }

    if (workTelephone) {
      phoneNumbers.push(workTelephone)
    }

    return (
      <StyledCarerItemContainer key={i}>
        <Avatar
          avatarSize="small"
          initials={[firstName, surname]}
          src={photo}
          title={`${firstName} ${surname}`}
        />
        <StyledCarerContact>
          {phoneNumbers.length ? (
            phoneNumbers.map((phoneNumber, index) => (
              <StyledCarerContactItem key={index}>
                <Icon height={18} icon="call" />
                <Typography>
                  {phoneNumber}
                </Typography>
              </StyledCarerContactItem>
            ))
          ) : (
            <Typography fontSize="14px" lineHeight={30} italic>
              {i18n.t('modals:Administration:AddEmergencyDose:Form:noContactLabel')}
            </Typography>
          )}
        </StyledCarerContact>
      </StyledCarerItemContainer>
    )
  }

  const renderBanner = () => {
    if (!child) {
      return null
    }

    if (child?.information?.canReceiveMedicine) {
      return (
        <StyledFieldWrapper>
          <Banner.Info>
            {i18n.t('modals:Administration:AddEmergencyDose:Form:canReceiveMedicineBanner')}
          </Banner.Info>
        </StyledFieldWrapper>
      )
    }

    return (
      <StyledFieldWrapper>
        <Banner.Error>
          {i18n.t('modals:Administration:AddEmergencyDose:Form:cannotReceiveMedicineBanner')}
        </Banner.Error>
      </StyledFieldWrapper>
    )
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Form.Row
        label={i18n.t('global:Date')}
        required
        verticalLabel
      >
        <Field
          component={Form.DatePicker}
          disabled={isDiaryContext}
          name="takenAt"
          validate={isRequired}
        />
      </Form.Row>
      {isResettingChild
        ? <Spinner size="small" />
        : (
          <Form.Row
            label={_.upperFirst(i18n.t('global:child'))}
            width={{ field: '300px' }}
            required
            verticalLabel
          >
            <Field
              component={Form.InfiniteDropdowns.RegisterChildren}
              date={formValues?.takenAt}
              disabled={isChildContext}
              name="child"
              validate={isRequired}
            />
          </Form.Row>
        )}
      {isChildDetailLoading
        ? <Spinner size="small" />
        : (
          <React.Fragment>
            {renderBanner()}
            {child ? (
              <StyledFieldWrapper>
                <Space space="16px" />
                <Hr />
                <StyledCarerContainer>
                  {child?.carerChildRelations?.length
                    ? _.map(child?.carerChildRelations, renderCarer)
                    : i18n.t('modals:Administration:AddEmergencyDose:Form:emptyContactBanner')}
                </StyledCarerContainer>
                <Hr />
                <Space space="16px" />
              </StyledFieldWrapper>
            ) : null}
          </React.Fragment>
        )}
      <Form.Row margin="0 0 10px">
        <Field
          component={Form.Checkbox}
          disabled={!child}
          label={i18n.t('modals:Administration:AddEmergencyDose:Form:wasParentContacted:label')}
          name="wasParentContacted"
        />
      </Form.Row>
      {wasParentContacted && (
        <Form.Row
          label={i18n.t('modals:Administration:AddEmergencyDose:Form:Parent:label')}
          width={{ field: '300px' }}
          required
          verticalLabel
        >
          {isChildDetailLoading
            ? <Spinner size="small" />
            : (
              <Field
                childId={formChild?.value}
                component={Form.InfiniteDropdowns.Carers}
                disabled={!child?.carerChildRelations?.length}
                name="parent"
                placeholder={i18n.t('modals:Administration:AddEmergencyDose:Form:Parent:placeholder')}
                validate={isRequired}
              />
            )}
        </Form.Row>
      )}
      <Form.Row
        label={i18n.t('global:Time')}
        required
        verticalLabel
      >
        <Field
          component={Form.TimePicker}
          name="timeOfAdministration"
          validate={isRequired}
        />
      </Form.Row>
      {renderMedicationDetails()}
      {renderSignature()}
      <Form.FooterActions
        isSubmitting={isSubmitting}
        submitLabel={i18n.t('global:Save')}
        onCancelClick={onCancelClick}
      />
    </Form>
  )
}

const validate = (fields: AddEmergencyDoseFormValues) => {
  const errors = {
    witness: null,
  }

  const { administratedBy, witness } = fields || {}

  if (administratedBy && witness && administratedBy.value === witness.value) {
    errors.witness = i18n.t('modals:Administration:AddEmergencyDose:Form:sameStaffSelectionValidation')
  }

  return errors
}

const enhance = compose(
  reduxForm<AddEmergencyDoseFormValues, AddEmergencyDoseModalFormProps>({ form: ADD_EMERGENCY_DOSE_FORM, validate }),
  connector,
)

export default enhance(AddEmergencyDoseModalForm)
